import { browser } from './browser';

interface TooltipPointer {
    x: number;
    y: number;
    rotation: number;
    hoverAreaWidth: number;
}

interface TooltipContainer {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface TooltipOffset {
    pointer: TooltipPointer;
    container: TooltipContainer;
}

interface TooltipConfig {
    tooltipMaxWidth: number;
    tooltipMaxWidthPropertyName: string;
    tooltipDistanceFromContainer: number;
    tooltipMinimalDistanceFromScreenSide: number;
    pointerWidth: number;
    pointerHeight: number;
    pointerMinHorizontalDistanceFromTooltipCorner: number;
    pointerMinVerticalDistanceFromTooltipCorner: number;
}

/**
 * Parameters for calculating tooltip offset position.
 * @interface
 * @property {DOMRect} containerClientRect - Bounding client rect of the container element.
 * @property {HTMLElement} tooltipElement - Reference to the tooltip element.
 * @property {DOMRect} tooltipClientRect - Bounding client rect of the tooltip element.
 * @property {TooltipPosition} tooltipPosition - Desired position of the tooltip.
 */
interface GetTooltipOffsetParams {
    containerClientRect: DOMRect;
    tooltipElement: HTMLElement;
    tooltipClientRect: DOMRect;
    tooltipPosition: TooltipPosition;
}

/**
 * Enum for tooltip positions.
 * @readonly
 * @enum {string}
 */
export enum TooltipPosition {
    TopCenter = 'topCenter',
    TopLeft = 'topLeft',
    TopRight = 'topRight',
    BottomCenter = 'bottomCenter',
    BottomLeft = 'bottomLeft',
    BottomRight = 'bottomRight',
    RightCenter = 'rightCenter',
    LeftCenter = 'leftCenter',
}

/** @type {TooltipConfig} */
const DefaultTooltipConfig: TooltipConfig = {
    tooltipMaxWidth: 360,
    tooltipMaxWidthPropertyName: '--ui-annotation-max-width',
    tooltipDistanceFromContainer: 20,
    tooltipMinimalDistanceFromScreenSide: 8,
    pointerWidth: 24,
    pointerHeight: 12,
    pointerMinHorizontalDistanceFromTooltipCorner: 11,
    pointerMinVerticalDistanceFromTooltipCorner: 16,
};

/**
 * Creates a clone of a hidden tooltip element in order to get its dimensions.
 * @param {HTMLElement} tooltipElement
 * @returns {DOMRect}
 */
const getTooltipElementRect = (tooltipElement: HTMLElement): DOMRect => {
    const tooltipElementClone = tooltipElement.cloneNode(true) as HTMLElement;
    tooltipElementClone.style.visibility = 'hidden';
    tooltipElementClone.style.position = 'absolute';
    document.body.appendChild(tooltipElementClone);
    const rect = tooltipElementClone.getBoundingClientRect();
    document.body.removeChild(tooltipElementClone);
    return rect;
};

/**
 * Gets position of the tooltip, how it should be rendered on the screen.
 * @param {DOMRect} containerElementClientRect
 * @param {DOMRect} tooltipElementClientRect
 * @returns {TooltipPosition}
 */
const getTooltipPosition = (
    containerElementClientRect: DOMRect,
    tooltipElementClientRect: DOMRect
): TooltipPosition => {
    const containerElementCenterPoint = {
        x: containerElementClientRect.x + containerElementClientRect.width / 2,
        y: containerElementClientRect.y + containerElementClientRect.height / 2,
    };

    const viewPortCenterPoint = {
        x: browser.run(() => document.documentElement.clientWidth / 2, 0),
        y: browser.run(() => window.innerHeight / 2, 0),
    };

    const tooltipCenterPosition =
        containerElementCenterPoint.y < viewPortCenterPoint.y
            ? TooltipPosition.BottomCenter
            : TooltipPosition.TopCenter;

    if (
        containerElementCenterPoint.x - tooltipElementClientRect.width / 2 >
            0 &&
        containerElementCenterPoint.x + tooltipElementClientRect.width / 2 <
            document.documentElement.clientWidth
    ) {
        return tooltipCenterPosition;
    }

    if (containerElementCenterPoint.x < viewPortCenterPoint.x) {
        return tooltipCenterPosition === TooltipPosition.BottomCenter
            ? TooltipPosition.BottomLeft
            : TooltipPosition.TopLeft;
    }

    return tooltipCenterPosition === TooltipPosition.BottomCenter
        ? TooltipPosition.BottomRight
        : TooltipPosition.TopRight;
};

/**
 * Calculates the tooltip offset regard to parent element and the pointer position.
 * @param {GetTooltipOffsetParams} params - The arguments object
 * @param {TooltipConfig} config - The config object
 * @returns {TooltipOffset}
 */
const getTooltipOffset = (
    params: GetTooltipOffsetParams,
    config: TooltipConfig
): TooltipOffset => {
    const {
        containerClientRect,
        tooltipElement,
        tooltipClientRect: tooltipElementInitial,
        tooltipPosition,
    } = params;
    const {
        tooltipMaxWidth,
        tooltipMaxWidthPropertyName,
        tooltipDistanceFromContainer,
        pointerWidth,
        pointerHeight,
        tooltipMinimalDistanceFromScreenSide,
        pointerMinHorizontalDistanceFromTooltipCorner,
        pointerMinVerticalDistanceFromTooltipCorner,
    } = config;
    let tooltipClientRect = tooltipElementInitial;

    switch (tooltipPosition) {
        case TooltipPosition.TopLeft: {
            const pointerX =
                (containerClientRect.width - pointerWidth) / 2 -
                (tooltipMinimalDistanceFromScreenSide - containerClientRect.x);
            if (pointerX < pointerMinHorizontalDistanceFromTooltipCorner) {
                return getTooltipOffset(
                    { ...params, tooltipPosition: TooltipPosition.RightCenter },
                    config
                );
            }

            const tooltipY = -(
                tooltipDistanceFromContainer +
                containerClientRect.height +
                tooltipClientRect.height
            );
            if (
                tooltipY <
                (containerClientRect.y + containerClientRect.height) * -1
            ) {
                return getTooltipOffset(
                    { ...params, tooltipPosition: TooltipPosition.BottomLeft },
                    config
                );
            }

            return {
                container: {
                    x:
                        tooltipMinimalDistanceFromScreenSide -
                        containerClientRect.x,
                    y: tooltipY,
                    width: tooltipClientRect.width,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: pointerX,
                    y: tooltipClientRect.height,
                    rotation: 0,
                    hoverAreaWidth: Math.min(
                        containerClientRect.width,
                        tooltipClientRect.width
                    ),
                },
            };
        }
        case TooltipPosition.TopCenter:
            const tooltipY = -(
                tooltipDistanceFromContainer +
                containerClientRect.height +
                tooltipClientRect.height
            );
            if (
                tooltipY <
                (containerClientRect.y + containerClientRect.height) * -1
            ) {
                return getTooltipOffset(
                    {
                        ...params,
                        tooltipPosition: TooltipPosition.BottomCenter,
                    },
                    config
                );
            }

            return {
                container: {
                    x:
                        (containerClientRect.width - tooltipClientRect.width) /
                        2,
                    y: -(
                        tooltipDistanceFromContainer +
                        containerClientRect.height +
                        tooltipClientRect.height
                    ),
                    width: tooltipClientRect.width,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: (tooltipClientRect.width - pointerWidth) / 2,
                    y: tooltipClientRect.height,
                    rotation: 0,
                    hoverAreaWidth: Math.min(
                        containerClientRect.width * 2,
                        tooltipClientRect.width
                    ),
                },
            };
        case TooltipPosition.TopRight: {
            const pointerX =
                tooltipClientRect.width -
                pointerWidth -
                (containerClientRect.width - pointerWidth) / 2 +
                (tooltipMinimalDistanceFromScreenSide -
                    (document.documentElement.clientWidth -
                        containerClientRect.right));
            if (
                pointerX >
                tooltipClientRect.width -
                    (pointerMinHorizontalDistanceFromTooltipCorner +
                        pointerWidth)
            ) {
                return getTooltipOffset(
                    { ...params, tooltipPosition: TooltipPosition.LeftCenter },
                    config
                );
            }

            const tooltipY = -(
                tooltipDistanceFromContainer +
                containerClientRect.height +
                tooltipClientRect.height
            );
            if (
                tooltipY <
                (containerClientRect.y + containerClientRect.height) * -1
            ) {
                return getTooltipOffset(
                    { ...params, tooltipPosition: TooltipPosition.BottomRight },
                    config
                );
            }

            return {
                container: {
                    x: -(
                        tooltipMinimalDistanceFromScreenSide -
                        (document.documentElement.clientWidth -
                            containerClientRect.right) +
                        (tooltipClientRect.width - containerClientRect.width)
                    ),
                    y: -(
                        tooltipDistanceFromContainer +
                        containerClientRect.height +
                        tooltipClientRect.height
                    ),
                    width: tooltipClientRect.width,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: pointerX,
                    y: tooltipClientRect.height,
                    rotation: 0,
                    hoverAreaWidth: Math.min(
                        containerClientRect.width,
                        tooltipClientRect.width
                    ),
                },
            };
        }
        case TooltipPosition.BottomLeft: {
            const pointerX =
                (containerClientRect.width - pointerWidth) / 2 -
                (tooltipMinimalDistanceFromScreenSide - containerClientRect.x);

            if (pointerX < pointerMinHorizontalDistanceFromTooltipCorner) {
                return getTooltipOffset(
                    { ...params, tooltipPosition: TooltipPosition.RightCenter },
                    config
                );
            }

            return {
                container: {
                    x:
                        tooltipMinimalDistanceFromScreenSide -
                        containerClientRect.x,
                    y: tooltipDistanceFromContainer,
                    width: tooltipClientRect.width,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: pointerX,
                    y: -pointerHeight,
                    rotation: 180,
                    hoverAreaWidth: Math.min(
                        containerClientRect.width,
                        tooltipClientRect.width
                    ),
                },
            };
        }
        case TooltipPosition.BottomCenter:
            return {
                container: {
                    x:
                        (containerClientRect.width - tooltipClientRect.width) /
                        2,
                    y: tooltipDistanceFromContainer,
                    width: tooltipClientRect.width,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: (tooltipClientRect.width - pointerWidth) / 2,
                    y: -pointerHeight,
                    rotation: 180,
                    hoverAreaWidth: Math.min(
                        containerClientRect.width * 2,
                        tooltipClientRect.width
                    ),
                },
            };
        case TooltipPosition.BottomRight: {
            const pointerX =
                tooltipClientRect.width -
                pointerWidth -
                (containerClientRect.width - pointerWidth) / 2 +
                (tooltipMinimalDistanceFromScreenSide -
                    (document.documentElement.clientWidth -
                        containerClientRect.right));

            if (
                pointerX >
                tooltipClientRect.width -
                    (pointerMinHorizontalDistanceFromTooltipCorner +
                        pointerWidth)
            ) {
                return getTooltipOffset(
                    { ...params, tooltipPosition: TooltipPosition.LeftCenter },
                    config
                );
            }

            return {
                container: {
                    x: -(
                        tooltipMinimalDistanceFromScreenSide -
                        (document.documentElement.clientWidth -
                            containerClientRect.right) +
                        (tooltipClientRect.width - containerClientRect.width)
                    ),
                    y: tooltipDistanceFromContainer,
                    width: tooltipClientRect.width,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: pointerX,
                    y: -pointerHeight,
                    rotation: 180,
                    hoverAreaWidth: Math.min(
                        containerClientRect.width,
                        tooltipClientRect.width
                    ),
                },
            };
        }
        case TooltipPosition.RightCenter: {
            const tooltipPossibleMaxWidth = Math.min(
                document.documentElement.clientWidth -
                    tooltipMinimalDistanceFromScreenSide -
                    containerClientRect.x -
                    containerClientRect.width -
                    tooltipDistanceFromContainer,
                tooltipMaxWidth
            );
            const tooltipWidth = Math.min(
                tooltipPossibleMaxWidth,
                tooltipClientRect.width
            );

            if (tooltipWidth < tooltipClientRect.width) {
                tooltipElement.style.setProperty(
                    tooltipMaxWidthPropertyName,
                    `${tooltipWidth}px`
                );
                tooltipClientRect = getTooltipElementRect(tooltipElement);
            }

            const tooltipYDeltaMin =
                tooltipMinimalDistanceFromScreenSide -
                (containerClientRect.y + containerClientRect.height);
            const tooltipYDeltaMax =
                browser.run(() => window.innerHeight, 0) -
                tooltipMinimalDistanceFromScreenSide -
                tooltipClientRect.height -
                (containerClientRect.y + containerClientRect.height);
            const tooltipYCenterDelta =
                -(containerClientRect.height + tooltipClientRect.height) / 2;
            const tooltipY = Math.max(
                Math.min(tooltipYCenterDelta, tooltipYDeltaMax),
                tooltipYDeltaMin
            );

            const pointerMaxYOffset =
                tooltipClientRect.height -
                pointerHeight -
                pointerMinVerticalDistanceFromTooltipCorner;

            return {
                container: {
                    x: containerClientRect.width + tooltipDistanceFromContainer,
                    y: tooltipY,
                    width: tooltipWidth,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: -(pointerWidth * 0.75),
                    y: Math.max(
                        Math.min(
                            (tooltipY +
                                containerClientRect.height -
                                pointerHeight * 0.25) *
                                -1,
                            pointerMaxYOffset
                        ),
                        pointerMinVerticalDistanceFromTooltipCorner
                    ),
                    rotation: 90,
                    hoverAreaWidth: Math.min(
                        containerClientRect.height * 2,
                        tooltipClientRect.height
                    ),
                },
            };
        }
        case TooltipPosition.LeftCenter: {
            const tooltipPossibleMaxWidth = Math.min(
                containerClientRect.x -
                    tooltipDistanceFromContainer -
                    tooltipMinimalDistanceFromScreenSide,
                tooltipMaxWidth
            );
            const tooltipWidth = Math.min(
                tooltipClientRect.width,
                tooltipPossibleMaxWidth
            );

            if (tooltipWidth < tooltipClientRect.width) {
                tooltipElement.style.setProperty(
                    tooltipMaxWidthPropertyName,
                    `${tooltipWidth}px`
                );
                tooltipClientRect = getTooltipElementRect(tooltipElement);
            }

            const tooltipYDeltaMin =
                tooltipMinimalDistanceFromScreenSide -
                (containerClientRect.y + containerClientRect.height);
            const tooltipYDeltaMax =
                browser.run(() => window.innerHeight, 0) -
                tooltipMinimalDistanceFromScreenSide -
                tooltipClientRect.height -
                (containerClientRect.y + containerClientRect.height);
            const tooltipYCenterDelta =
                -(containerClientRect.height + tooltipClientRect.height) / 2;
            const tooltipY = Math.max(
                Math.min(tooltipYCenterDelta, tooltipYDeltaMax),
                tooltipYDeltaMin
            );

            const pointerMaxYOffset =
                tooltipClientRect.height -
                pointerHeight -
                pointerMinVerticalDistanceFromTooltipCorner;

            return {
                container: {
                    x: -(tooltipWidth + tooltipDistanceFromContainer),
                    y: tooltipY,
                    width: tooltipWidth,
                    height: tooltipClientRect.height,
                },
                pointer: {
                    x: tooltipWidth - pointerWidth * 0.25,
                    y: Math.max(
                        Math.min(
                            (tooltipY +
                                containerClientRect.height -
                                pointerHeight * 0.25) *
                                -1,
                            pointerMaxYOffset
                        ),
                        pointerMinVerticalDistanceFromTooltipCorner
                    ),
                    rotation: -90,
                    hoverAreaWidth: Math.min(
                        containerClientRect.height * 2,
                        tooltipClientRect.height
                    ),
                },
            };
        }
    }
};

/**
 * Calculates the necessary offset of the tooltip relative to the
 * current position and the desired end position.
 * @param {HTMLElement} containerElement
 * @param {HTMLElement} tooltipElement
 * @param {TooltipConfig} [config]
 * @returns {TooltipOffset}
 */
export const calculateTooltipOffset = (
    containerElement: HTMLElement,
    tooltipElement: HTMLElement,
    config: Partial<TooltipConfig> = {}
): TooltipOffset => {
    const containerClientRect = containerElement.getBoundingClientRect();
    const tooltipClientRect = getTooltipElementRect(tooltipElement);
    const tooltipPosition = getTooltipPosition(
        containerClientRect,
        tooltipClientRect
    );
    return getTooltipOffset(
        {
            containerClientRect,
            tooltipElement,
            tooltipClientRect,
            tooltipPosition,
        },
        { ...DefaultTooltipConfig, ...config }
    );
};

/**
 * @typedef TooltipPointer
 * @property {number} x  X-coordinate of the pointer.
 * @property {number} y  Y-coordinate of the pointer.
 * @property {number} rotation  Y-coordinate of the pointer.
 * @property {number} hoverAreaWidth  Y-coordinate of the pointer.
 */

/**
 * @typedef TooltipContainer
 * @property {number} x - X-coordinate of the tooltip.
 * @property {number} y - Y-coordinate of the tooltip.
 * @property {number} width - Width of the tooltip.
 * @property {number} height - Height of the tooltip.
 */

/**
 * @typedef {object} TooltipOffset
 * @property {TooltipPointer} pointer - Pointer.
 * @property {TooltipContainer} container - Tooltip.
 */

/**
 * Calculates the tooltip offset regard to parent element and the pointer position.
 * @param {object} params - The arguments object
 * @param {DOMRect} params.containerClientRect
 * @param {HTMLElement} params.tooltipElement
 * @param {DOMRect} params.tooltipClientRect
 * @param {TooltipPosition} params.tooltipPosition
 * @param {TooltipConfig} config - The config object
 * @returns {TooltipOffset}
 */

/**
 * @typedef {('topCenter'|'topLeft'|'topRight'|'bottomCenter'|'bottomLeft'|'bottomRight'|'rightCenter'|'leftCenter')} TooltipPosition
 */

/**
 * Default configuration for tooltip positioning and sizing.
 * @typedef {object} TooltipConfig
 * @property {number} tooltipMaxWidth - Maximum width of the tooltip.
 * @property {string} tooltipMaxWidthPropertyName - CSS custom property name for setting the tooltip's max width.
 * @property {number} tooltipDistanceFromContainer - Distance between the tooltip and its container.
 * @property {number} tooltipMinimalDistanceFromScreenSide - Minimum distance between the tooltip and the edge of the screen.
 * @property {number} pointerWidth - Width of the tooltip's pointer.
 * @property {number} pointerHeight - Height of the tooltip's pointer.
 * @property {number} pointerMinHorizontalDistanceFromTooltipCorner - Minimum horizontal distance between the pointer and the corner of the tooltip.
 * @property {number} pointerMinVerticalDistanceFromTooltipCorner - Minimum vertical distance between the pointer and the corner of the tooltip.
 */

/**
 * @typedef {object} TooltipPositioningParams
 * @property {GetTooltipOffsetParams} params - The arguments object
 * @property {DOMRect} containerClientRect - container rectangle
 * @property {HTMLElement} tooltipElement - reference to the tooltip element
 * @property {DOMRect} tooltipClientRect - tooltip rectangle
 * @property {TooltipPosition} tooltipPosition - preferred tooltip position
 */
